import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useVersionWatcher — polls /api/version every 3 minutes and shows a
 * persistent "New version available" toast (with a Reload button) when
 * the server's build_stamp changes versus the one we saw on first load.
 *
 * This is the belt-and-suspenders fix for the stale-bundle problem the
 * user had to work around with DevTools hard-refresh after every deploy.
 *
 * How it works:
 *   1. First poll records the "baseline" stamp.
 *   2. Every subsequent poll compares — when stamps differ, a sonner
 *      toast with `duration: Infinity` pins to the screen with a Reload
 *      action. Clicking it calls `window.location.reload(true)` which
 *      bypasses the browser's memory cache. Combined with the
 *      no-cache meta tags we added to index.html, this guarantees the
 *      new bundle loads immediately.
 *   3. Also pokes service-worker unregister for any orphan SW a prior
 *      build might have left behind (we don't ship one, but belt AND
 *      suspenders).
 */
const POLL_MS = 3 * 60 * 1000; // 3 minutes

export default function useVersionWatcher() {
  const baselineRef = useRef(null);
  const toastShownRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    // Defensive: unregister any orphan service workers. We don't ship one
    // in this app; this protects users coming back to a redeploy after
    // an earlier app version registered a SW that could serve stale HTML.
    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((reg) => { try { reg.unregister(); } catch { /* ignore */ } });
      }).catch(() => { /* ignore */ });
    }

    const check = async () => {
      try {
        const r = await axios.get(`${API}/version`, {
          timeout: 6000,
          // Bypass any cache layers between us and the backend.
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
          params: { _: Date.now() },
        });
        const stamp = r?.data?.build_stamp;
        if (cancelled || !stamp) return;
        if (baselineRef.current == null) {
          baselineRef.current = stamp;
          return;
        }
        if (stamp !== baselineRef.current && !toastShownRef.current) {
          toastShownRef.current = true;
          toast('New version available', {
            description:
              "We just shipped an update. Reload to pick up the latest DiviDrip — your work is safe.",
            duration: Infinity,
            action: {
              label: 'Reload',
              onClick: () => {
                try { window.location.reload(true); } catch { window.location.reload(); }
              },
            },
          });
        }
      } catch {
        /* transient network/DNS blip — try again next tick */
      }
    };

    check();
    const id = setInterval(check, POLL_MS);
    // Also re-check when the tab regains focus (common path: phone wakes
    // from screen-off with a stale tab).
    const onFocus = () => { check(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
}
